import { ArrowRight, Shield, Receipt, Settings, FileText } from 'lucide-react';

const Hero = ({ onWaitlistClick, onDemoClick }) => {
  return (
    <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
      {/* Background gradient glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-indigo-600/10 rounded-full blur-3xl"></div>
      
      <div className="container mx-auto relative z-10">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left content */}
          <div className="space-y-8">
            <div className="space-y-6">
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold heading-font text-white leading-tight">
                Control business spend.
                <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-cyan-400">
                  Stay audit-ready.
                </span>
              </h1>
              <p className="md:text-xl max-w-xl !text-lg text-gray-400">Set budgets, issue team cards, capture receipts, and export clean records, without the busywork.

              </p>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={onWaitlistClick}
                className="group px-8 py-4 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-xl transition-all inline-flex items-center justify-center gap-2 shadow-lg shadow-indigo-600/20">

                Join waitlist
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={onDemoClick}
                className="px-8 py-4 bg-white/5 hover:bg-white/10 text-white font-medium rounded-xl transition-colors backdrop-blur-sm border border-white/10">

                Request demo
              </button>
            </div>

            {/* Trust line */}
            <div className="flex flex-wrap items-center gap-2 text-sm text-gray-400 pt-4 pb-2">
              <span>Built for freelancers & small businesses</span>
              <span className="text-gray-600">•</span>
              <span>Spend controls</span>
              <span className="text-gray-600">•</span>
              <span>Audit-ready activity</span>
            </div>

            {/* Trust chips */}
            <div className="flex flex-wrap gap-3">
              <div className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-lg border border-white/10 backdrop-blur-sm">
                <Shield size={16} className="text-indigo-400" />
                <span className="text-sm text-gray-300">Spend limits</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-lg border border-white/10 backdrop-blur-sm">
                <Receipt size={16} className="text-indigo-400" />
                <span className="text-sm text-gray-300">Receipt capture</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-lg border border-white/10 backdrop-blur-sm">
                <Settings size={16} className="text-indigo-400" />
                <span className="text-sm text-gray-300">Admin controls</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-lg border border-white/10 backdrop-blur-sm">
                <FileText size={16} className="text-indigo-400" />
                <span className="text-sm text-gray-300">Audit trail</span>
              </div>
            </div>

            {/* Not a bank disclaimer */}
            <p className="text-xs text-gray-500 pt-6">
              Limitly is a spend management platform. Financial services are provided by regulated financial institution partners.
            </p>
          </div>

          {/* Right side - Card visual */}
          <div className="relative">
            <div className="relative w-full aspect-square max-w-lg mx-auto">
              {/* Glowing card */}
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/20 to-cyan-600/20 rounded-3xl blur-2xl"></div>
              <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl border border-white/10 p-8 shadow-2xl">
                {/* Card mockup */}
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-indigo-600 to-cyan-600 rounded-2xl p-6 shadow-lg">
                    <div className="flex justify-between items-start mb-12">
                      <div className="w-12 h-8 bg-white/20 rounded backdrop-blur-sm"></div>
                      <div className="text-right">
                        <div className="text-xs text-white/60 mb-1">LIMITLY</div>
                        <div className="text-sm font-semibold text-white">BUSINESS</div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="text-2xl font-bold text-white tracking-wider">•••• 4242</div>
                      <div className="flex justify-between">
                        <div>
                          <div className="text-xs text-white/60">Cardholder</div>
                          <div className="text-sm font-medium text-white">Team Member</div>
                        </div>
                        <div>
                          <div className="text-xs text-white/60">Limit</div>
                          <div className="text-sm font-medium text-white">$5,000</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* App UI elements */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-4 bg-white/5 rounded-xl border border-white/10">
                      <div className="w-10 h-10 bg-indigo-600/20 rounded-lg flex items-center justify-center">
                        <Receipt size={20} className="text-indigo-400" />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-white">Office supplies</div>
                        <div className="text-xs text-gray-400">$247.50 • Today</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-4 bg-white/5 rounded-xl border border-white/10">
                      <div className="w-10 h-10 bg-cyan-600/20 rounded-lg flex items-center justify-center">
                        <Receipt size={20} className="text-cyan-400" />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-white">Software subscription</div>
                        <div className="text-xs text-gray-400">$99.00 • Yesterday</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default Hero;